import {ASSET_ENV_REGISTRY} from './registry-data';
import type {AssetEntry} from './getCurrentAssetEntry';

export type S3CdnMapExtra = {
  readonly s3bucket: string;
  readonly cloudFlareCdn: string;
};

export type S3CdnMap = {
  readonly s3ToCfCdn: ReadonlyMap<string, string>;
  readonly cdnToS3: ReadonlyMap<string, string>;
  readonly wfCdnToS3: ReadonlyMap<string, string>;
  readonly allCdnRegex: RegExp;
  readonly wfCdnRegex: RegExp;
};

function escapeForRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function cdnRoot(host: string): string {
  return `https://${host}/`;
}

function s3Root(bucket: string, host?: string): string {
  // eslint-disable-next-line webflow/no-hardcoded-s3-endpoints -- legacy host: US default when the registry entry has no s3Host
  return `https://${host || 's3.amazonaws.com'}/${bucket}/`;
}

export function buildS3CdnMap(
  registry: Readonly<Record<string, AssetEntry>>,
  extras?: readonly S3CdnMapExtra[]
): S3CdnMap {
  const s3ToCfCdn = new Map<string, string>();
  const cdnToS3 = new Map<string, string>();
  const wfCdnToS3 = new Map<string, string>();
  const bucketSeen = new Set<string>();

  function addCdn(host: string | undefined, s3: string): void {
    if (host) {
      const root = cdnRoot(host);
      if (!cdnToS3.has(root)) {
        cdnToS3.set(root, s3);
      }
    }
  }

  function addWfCdn(host: string | undefined, s3: string): void {
    if (host) {
      const root = cdnRoot(host);
      if (!wfCdnToS3.has(root)) {
        wfCdnToS3.set(root, s3);
      }
      addCdn(host, s3);
    }
  }

  for (const entry of Object.values(registry)) {
    const s3 = s3Root(entry.s3bucket, entry.s3Host);

    if (!bucketSeen.has(entry.s3bucket)) {
      bucketSeen.add(entry.s3bucket);
      const cfRoot = cdnRoot(entry.cloudFlareCdn);
      s3ToCfCdn.set(s3, cfRoot);
      // Stored URLs for a regional bucket can still carry the legacy host.
      if (entry.s3Host) {
        s3ToCfCdn.set(s3Root(entry.s3bucket), cfRoot);
      }
    }

    addWfCdn(entry.cloudFlareCdn, s3);
    addWfCdn(entry.sslCdn, s3);
    if (entry.oldCdn) {
      for (const old of entry.oldCdn) {
        addWfCdn(old, s3);
      }
    }

    addCdn(entry.sslCdnWl, s3);
    addCdn(entry.globalSslCdn, s3);
    addCdn(entry.globalSslCdnWl, s3);
    addCdn(entry.fileSslCdn, s3);
    addCdn(entry.cdn, s3);
  }

  if (extras) {
    for (const extra of extras) {
      const s3 = s3Root(extra.s3bucket);
      const cf = cdnRoot(extra.cloudFlareCdn);
      if (!bucketSeen.has(extra.s3bucket)) {
        bucketSeen.add(extra.s3bucket);
        s3ToCfCdn.set(s3, cf);
      }
      if (!cdnToS3.has(cf)) {
        cdnToS3.set(cf, s3);
      }
      if (!wfCdnToS3.has(cf)) {
        wfCdnToS3.set(cf, s3);
      }
    }
  }

  const allCdnRoots = [...cdnToS3.keys()];
  const wfCdnRoots = [...wfCdnToS3.keys()];

  const allCdnRegex =
    allCdnRoots.length > 0
      ? new RegExp(allCdnRoots.map(escapeForRegex).join('|'), 'g')
      : /(?!)/g;

  const wfCdnRegex =
    wfCdnRoots.length > 0
      ? new RegExp(wfCdnRoots.map(escapeForRegex).join('|'), 'g')
      : /(?!)/g;

  return {s3ToCfCdn, cdnToS3, wfCdnToS3, allCdnRegex, wfCdnRegex};
}

export const REGISTRY_S3_CDN_MAP: S3CdnMap = buildS3CdnMap(ASSET_ENV_REGISTRY);
